import './i18n';
import { APP_VERSION } from './version';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MatrixRain from './components/MatrixRain';
import AscLogo from './components/AscLogo';
import VoiceOrb from './components/VoiceOrb';
import LoginScreen from './components/LoginScreen';
import AdminPanel from './components/AdminPanel';
import { CalendarPanel, TaskPanel, GroupShoppingPanel, GroupChatPanel, ContactsPanel, OrdersPanel, Notification } from './components/Panels';
import { NotificationBell, NotificationPanel, NotificationToast } from './components/NotificationPopup';
import UserSettings from './components/UserSettings';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import { useVoice } from './hooks/useVoice';
import { useWakeWord } from './hooks/useWakeWord';
import { useNotifications } from './hooks/useNotifications';
import { useShopping, useGroupShopping, useCalendar, useSharedTasks, useConversation, useSettings, useContacts, useGroupSettings, useOrders, useProductsDb } from './hooks/useStorage';
import { askSirus, parseActions } from './services/anthropic';
import { getStoredUser, logout, storeSession, clearSession, checkSession } from './services/auth';
import './App.css';

export default function App() {
  const { t, i18n } = useTranslation();
  const [user, setUser]           = useState(() => getStoredUser());
  const [panel, setPanel]         = useState(null);
  const [nav, setNav]             = useState('home');
  const [volume, setVolume]       = useState(0);
  const [userText, setUserText]   = useState('');
  const [apiError, setApiError]   = useState('');
  const [textInput, setTextInput] = useState('');
  const [isTyping, setIsTyping]   = useState(false);
  const textRef = useRef(null);
  // Disambiguazione prodotto in sospeso (SA-SHOP-DBMATCH-01): candidati mostrati
  // in attesa che l'utente indichi quale inserire nel prossimo messaggio.
  const pendingAddRef = useRef(null);

  // Shared data — hook con enabled=!!user: nessuna fetch/write remota prima del login (evita 401 inutili)
  const { events, addEvent, editEvent, deleteEvent }         = useCalendar(user);
  const { tasks, addTask, toggleTask, deleteTask, editTask } = useSharedTasks(user);
  const { items, addItem, removeItem, editItem }             = useShopping(user);
  const { items: gItems, addItem: gAdd, removeItem: gRemove, editItem: gEdit } = useGroupShopping(user);
  const { products: productsDb, setProducts: setProductsDb }                  = useProductsDb(user);
  const { contacts, addContact, editContact, deleteContact }                   = useContacts(user);
  const { orders, addOrder, updateOrder, completeOrder, deleteOrder }          = useOrders(user);
  const { groupName }                                                          = useGroupSettings(user);
  const { messages, addMessage }                             = useConversation();
  const { wakeWordEnabled, setWakeWordEnabled }              = useSettings(user);
  const { notifications, groupUsers, pendingCount, toast, sendNotification, respondNotification }
                                                             = useNotifications(user);
  const [showNotifs,    setShowNotifs]    = useState(false);
  const [showSettings,  setShowSettings]  = useState(false);
  const isOnline = useOnlineStatus(user);

  const lastMsg = messages[messages.length - 1];

  // Verifica sessione al mount con il backend (SA-012B): priorità cookie HttpOnly, fallback Bearer.
  // sirus_user in localStorage è usato solo per mostrare l'UI immediatamente; il backend è l'arbitro.
  useEffect(() => {
    (async () => {
      const verified = await checkSession();
      if (verified) {
        setUser(verified);
      } else {
        await clearSession();
        setUser(null);
      }
    })();
  }, []);

  // Clock
  const [clock, setClock]  = useState('');
  const [dateStr, setDate] = useState('');
  useEffect(() => {
    const upd = () => {
      const n = new Date();
      setClock(`${String(n.getHours()).padStart(2,'0')}:${String(n.getMinutes()).padStart(2,'0')}`);
      const D=['DOM','LUN','MAR','MER','GIO','VEN','SAB'];
      const M=['GEN','FEB','MAR','APR','MAG','GIU','LUG','AGO','SET','OTT','NOV','DIC'];
      setDate(`${D[n.getDay()]} ${n.getDate()} ${M[n.getMonth()]}`);
    };
    upd(); const iv=setInterval(upd,30000); return()=>clearInterval(iv);
  }, []);

  // Oggi per notifiche
  const today = new Date().toISOString().split('T')[0];
  const todayEvents = (Array.isArray(events) ? events : []).filter(e => e.date === today);

  const norm = (v='') => String(v).toLowerCase().replace(/[?.,;:!]+$/g, '').replace(/\s+/g, ' ').trim();
  const shopLine = (i) =>
    `${i.code || 'Codice non presente'} | ${i.name} | ${i.qty || ''}${i.destination ? ' | ' + i.destination : ''}`;
  const findShopItem = useCallback((list, ref='') => {
    const q = norm(ref);
    if (!q) return null;
    return (Array.isArray(list) ? list : []).find(i =>
      norm(i.code) === q || norm(i.name).includes(q) || q.includes(norm(i.name))
    ) || null;
  }, []);
  const findSupplierItems = useCallback((list, ref='') => {
    const q = norm(ref);
    if (!q) return [];
    return (Array.isArray(list) ? list : []).filter(i => {
      const s = norm(i.supplier);
      return s && (s === q || s.includes(q) || q.includes(s));
    });
  }, []);

  // ── Lookup Database prodotti prima di aggiungere alla lista (SA-SHOP-DBMATCH-01) ──
  const findProductMatches = useCallback((list, query) => {
    const q = norm(query);
    if (!q) return [];
    return (Array.isArray(list) ? list : []).filter(p => {
      const n = norm(p.name);
      return n && (n.includes(q) || q.includes(n));
    });
  }, []);
  const ORDINALI_IT = ['primo','secondo','terzo','quarto','quinto','sesto','settimo','ottavo','nono','decimo'];
  // Risolve la risposta dell'utente ("il secondo", "2", un codice o un nome) contro
  // i candidati proposti in precedenza. Ritorna il prodotto scelto o null se ambiguo.
  const resolveProductChoice = useCallback((replyText, candidates) => {
    const q = norm(replyText);
    if (!q || !Array.isArray(candidates) || !candidates.length) return null;
    const ordIdx = ORDINALI_IT.findIndex(w => q.includes(w));
    if (ordIdx !== -1 && candidates[ordIdx]) return candidates[ordIdx];
    const numMatch = q.match(/\b(\d+)\b/);
    if (numMatch) {
      const idx = parseInt(numMatch[1], 10) - 1;
      if (candidates[idx]) return candidates[idx];
    }
    const byCode = candidates.find(c => c.code && norm(c.code) === q);
    if (byCode) return byCode;
    const byName = candidates.filter(c => norm(c.name).includes(q) || q.includes(norm(c.name)));
    if (byName.length === 1) return byName[0];
    return null;
  }, []);

  const getShoppingAnswer = useCallback((text, list) => {
    const l = norm(text);
    if (!l) return '';
    const cleanText = text.replace(/[?.,;:!]+$/g, '');
    const supplierMatch = cleanText.match(/(?:da|dal|fornitore|ordine|comprare da|compro da)\s+([a-z0-9\s._-]+)$/i);
    if (supplierMatch && /(cosa|lista|ordine|compra|manca|prendere|compro)/i.test(text)) {
      const supplier = supplierMatch[1].trim();
      const rows = findSupplierItems(list, supplier);
      if (!rows.length) return 'Nessun prodotto trovato per questo fornitore.';
      return `ORDINE FORNITORE: ${rows[0].supplier || supplier}\n\nCodice | Prodotto | Quantità | Note\n${rows.map(shopLine).join('\n')}`;
    }
    const codeMatch = text.match(/codice\s+([a-z0-9._-]+)/i);
    if (codeMatch && /(cosa|che|prodotto)/i.test(text)) {
      const item = findShopItem(list, codeMatch[1]);
      if (!item) return 'Nessun prodotto trovato per questo codice.';
      return `Codice ${item.code || codeMatch[1]}:\nProdotto: ${item.name}\nFornitore: ${item.supplier || 'Fornitore non presente.'}`;
    }
    const productCodeMatch = cleanText.match(/(?:codice dei?|qual .*codice|codice\?)\s*(?:di|dei|del|della)?\s*([a-z0-9\s._-]+)?/i);
    const supplierFromProduct = /(dove compro|da quale fornitore|fornitore).*?([a-z0-9\s._-]+)$/i.exec(cleanText);
    const beforeCodeQuestion = /(qual|codice)/i.test(text)
      ? text.replace(/^sirus\s+/i, '').split(/,?\s*(qual|codice)/i)[0]
      : '';
    const productRef = supplierFromProduct?.[2] || productCodeMatch?.[1] || beforeCodeQuestion;
    if ((productCodeMatch || supplierFromProduct) && productRef) {
      const item = findShopItem(list, productRef.replace(/[?.,]+$/g, ''));
      if (!item) return 'Nessun prodotto trovato.';
      return `${item.name}:\nCodice: ${item.code || 'Codice non presente.'}\nFornitore: ${item.supplier || 'Fornitore non presente.'}`;
    }
    return '';
  }, [findShopItem, findSupplierItems]);

  const findOrderByRef = useCallback((ordList, ref) => {
    if (!ref) return null;
    const low = ref.toLowerCase();
    // "ultimo ordine" o "last order"
    if (/ultimo|last/i.test(low)) return ordList[0] || null;
    // "tavolo X" o numero puro
    const tMatch = low.match(/tavolo\s*(\w+)/i) || ((/^\w+$/.test(ref)) ? [null, ref] : null);
    if (tMatch) {
      const tNum = tMatch[1].trim();
      const byTable = ordList.filter(o => o.numero_tavolo === tNum && o.status === 'active');
      if (byTable.length === 1) return byTable[0];
      if (byTable.length > 1) return byTable[0]; // più recente
    }
    // fallback: id esatto
    return ordList.find(o => String(o.id) === ref) || null;
  }, []);

  const handleInput = useCallback(async (text) => {
    if (!text.trim()) return;
    setUserText(text); setApiError('');
    addMessage('user', text);

    // Risposta a una disambiguazione prodotto in sospeso (SA-SHOP-DBMATCH-01)
    if (pendingAddRef.current) {
      const pending = pendingAddRef.current;
      pendingAddRef.current = null;
      const chosen = resolveProductChoice(text, pending.candidates);
      if (chosen) {
        const adder = pending.isGroup ? gAdd : addItem;
        adder(chosen.name, pending.qty, pending.destination || '', chosen.supplier || '', '', chosen.code || '');
        const conf = `Aggiunto alla lista: ${chosen.code ? chosen.code + ' - ' : ''}${chosen.name}${chosen.supplier ? ' (' + chosen.supplier + ')' : ''}.`;
        addMessage('assistant', conf);
        voiceHook.speak(conf);
        return;
      }
      // Nessuna corrispondenza chiara: annulla la disambiguazione e prosegue
      // trattando il messaggio come una richiesta nuova, per non restare bloccati.
    }

    const history = messages.slice(-8).map(m=>({role:m.role,content:m.content}));
    history.push({ role:'user', content:text });
    const shoppingAnswer = getShoppingAnswer(text, gItems);
    if (shoppingAnswer) {
      addMessage('assistant', shoppingAnswer);
      voiceHook.speak(shoppingAnswer);
      return;
    }
    try {
      const raw = await askSirus(history, { events: todayEvents, tasks, shopping: gItems, orders });
      const { clean, actions } = parseActions(raw);
      const lowerText = text.toLowerCase();
      const wantsGroup = ['group','gruppo','condivisa','condiviso','gruppo shop','group shop']
        .some(kw => lowerText.includes(kw));
      const findByText = (arr, key, query) =>
        arr.find(x => String(x[key] || '').toLowerCase().includes(String(query || '').toLowerCase()));
      let disambiguationMsg = null;
      actions.forEach(a => {
        // Se l'utente chiede group ma Sirus usa ADD_SHOP, correggiamo
        if (a.type==='ADD_SHOP' && wantsGroup) a = { ...a, type: 'ADD_GSHOP' };
        if (a.type==='ADD_TASK')  addTask(a.params[0], a.params[1]||'media');
        if (a.type==='ADD_SHOP' || a.type==='ADD_GSHOP') {
          // SA-SHOP-DBMATCH-01: prima cerca nel Database prodotti condiviso —
          // 1 corrispondenza -> usa quella (codice/fornitore inclusi);
          // 2+ -> chiede quale inserire invece di aggiungere alla cieca;
          // 0, o codice già esplicito -> comportamento invariato (testo libero).
          const name         = a.params[0];
          const qty           = parseInt(a.params[1]) || 1;
          const explicitCode  = (a.params[2] || '').trim();
          const supplier      = a.params[3] || '';
          const destination   = a.params[5] || '';
          const isGroup       = a.type === 'ADD_GSHOP';
          const adder         = isGroup ? gAdd : addItem;
          const matches       = explicitCode ? [] : findProductMatches(productsDb, name);
          if (matches.length === 1) {
            const m = matches[0];
            adder(m.name, qty, destination, m.supplier || '', '', m.code || '');
          } else if (matches.length > 1) {
            const shown = matches.slice(0, 5);
            const list  = shown.map((m,i) => `${i+1}) ${m.name}${m.supplier ? ' (' + m.supplier + ')' : ''}`).join(', ');
            const more  = matches.length > shown.length ? ` (e altri ${matches.length - shown.length})` : '';
            disambiguationMsg = `Ho trovato più prodotti per "${name}": ${list}${more}. Quale vuoi aggiungere? Dimmi il numero o il nome esatto.`;
            pendingAddRef.current = { candidates: shown, qty, destination, isGroup };
          } else {
            adder(name, qty, destination, supplier, '', explicitCode);
          }
        }
        if (a.type==='ADD_EVENT') addEvent(a.params[0], a.params[1]||today, a.params[2]||'');
        if (a.type==='DELETE_TASK') {
          const f = findByText(tasks, 'text', a.params[0]);
          if (f) deleteTask(f.id);
        }
        if (a.type==='EDIT_TASK') {
          const f = findByText(tasks, 'text', a.params[0]);
          if (f) editTask(f.id, a.params[1]||f.text, a.params[2]||f.priority);
        }
        if (a.type==='DELETE_EVENT') {
          const f = findByText(events, 'title', a.params[0]);
          if (f) deleteEvent(f.id);
        }
        if (a.type==='EDIT_EVENT') {
          const f = findByText(events, 'title', a.params[0]);
          if (f) editEvent(f.id, { title: a.params[1]||f.title, date: a.params[2]||f.date, time: a.params[3]||f.time });
        }
        if (a.type==='DELETE_SHOP') {
          const f = findShopItem(items, a.params[0]);
          if (f) removeItem(f.id);
        }
        if (a.type==='EDIT_SHOP') {
          const f = findShopItem(items, a.params[0]);
          if (f) editItem(f.id, { name: a.params[1]||f.name, qty: parseInt(a.params[2])||f.qty, code: a.params[3]||f.code||'', supplier: a.params[4]||f.supplier||'', destination: a.params[5]||f.destination||'' });
        }
        if (a.type==='DELETE_GSHOP') {
          const f = findShopItem(gItems, a.params[0]);
          if (f) gRemove(f.id);
        }
        if (a.type==='EDIT_GSHOP') {
          const f = findShopItem(gItems, a.params[0]);
          if (f) gEdit(f.id, { name: a.params[1]||f.name, qty: parseInt(a.params[2])||f.qty, code: a.params[3]||f.code||'', supplier: a.params[4]||f.supplier||'', destination: a.params[5]||f.destination||'' });
        }
        if (a.type==='MOVE_TO_GROUP') {
          const found = items.find(i => i.name.toLowerCase() === a.params[0].toLowerCase());
          if (found) { gAdd(found.name, found.qty, found.destination, found.supplier, found.buyDate); removeItem(found.id); }
        }
        if (a.type==='MOVE_TO_SHOP') {
          const found = gItems.find(i => i.name.toLowerCase() === a.params[0].toLowerCase());
          if (found) { addItem(found.name, found.qty, found.destination, found.supplier, found.buyDate); gRemove(found.id); }
        }
        // ── Ordini ──────────────────────────────────────────────────────────────
        if (a.type==='CREATE_ORDER') {
          addOrder({
            tipo:          a.params[0] || '',
            numero_tavolo: a.params[1] || '',
            starter:       a.params[2] || '',
            main:          a.params[3] || '',
            dessert:       a.params[4] || '',
            note:          a.params[5] || '',
            created_by:    user.username,
          });
        }
        if (a.type==='COMPLETE_ORDER') {
          if (user.role === 'admin') {
            const ref = (a.params[0] || '').trim();
            const found = findOrderByRef(orders, ref);
            if (found) completeOrder(found.id, user.username);
          }
        }
        if (a.type==='UPDATE_ORDER') {
          const ref   = (a.params[0] || '').trim();
          const field = (a.params[1] || '').trim();
          const value = a.params.slice(2).join(':').trim();
          const found = findOrderByRef(orders, ref);
          if (found && field) updateOrder(found.id, { [field]: value });
        }
        if (a.type==='DELETE_ORDER') {
          if (user.role === 'admin') {
            const ref = (a.params[0] || '').trim();
            const found = findOrderByRef(orders, ref);
            if (found) deleteOrder(found.id);
          }
        }
      });
      const finalMsg = disambiguationMsg || clean;
      addMessage('assistant', finalMsg);
      voiceHook.speak(finalMsg);
    } catch (e) {
      const msg = e.message?.includes('401') ? 'Sessione scaduta — rieffettua il login'
                : e.message?.includes('429') ? 'Rate limit — riprova tra poco'
                : `Errore: ${e.message}`;
      setApiError(msg);
      if (e.message?.includes('401')) setTimeout(logout, 2000);
      addMessage('assistant', '[Errore di connessione]');
      voiceHook.setIdle();
    }
  }, [todayEvents, tasks, events, items, gItems, messages, addMessage, addTask, deleteTask, editTask, addItem, editItem, gAdd, gEdit, removeItem, gRemove, addEvent, deleteEvent, editEvent, today, orders, addOrder, updateOrder, completeOrder, deleteOrder, findOrderByRef, findShopItem, getShoppingAnswer, productsDb, findProductMatches, resolveProductChoice, user]);

  const voiceHook = useVoice({ onTranscript: handleInput, onVolumeChange: setVolume, lang: i18n.language });

  const sendText = useCallback(() => {
    const t = textInput.trim(); if (!t) return;
    setTextInput(''); setIsTyping(false); handleInput(t);
  }, [textInput, handleInput]);

  const handleWake = useCallback(() => {
    if (voiceHook.state === 'idle') voiceHook.toggle();
  }, [voiceHook]);

  const { listening: wakeListening, supported: wakeSupported } = useWakeWord({
    enabled: !!user && wakeWordEnabled && voiceHook.state === 'idle',
    onWake:  handleWake,
    lang:    i18n.language,
  });

  const openPanel = (name) => { setPanel(p=>p===name?null:name); setNav(name); };
  const roleBadge = { admin:'ADMIN', operator:'OPR', readonly:'READ' };

  if (!user) return (<><MatrixRain/><LoginScreen onLogin={setUser}/></>);

  return (
    <div className="app">
      <MatrixRain />

      <header className="hdr">
        <div className="hdr-logo">
          <AscLogo size={44}/>
          <div className="hdr-titles">
                    <div className="hdr-name">
                    <div>SIRUS<span className="hdr-ver-tag"> {APP_VERSION}</span></div>
                    <div className="hdr-assistant">ASSISTANT</div>
                  </div>
            {groupName && <div className="hdr-group">{groupName}</div>}
          </div>
        </div>
        <div className="hdr-right">
          <div className="clock-row">
            <div className="clock">{clock}</div>
            <div className="date-str">{dateStr}</div>
          </div>
          <div className="user-row">
            <span className="user-name">{user.username}</span>
            <span className={`role-badge role-${user.role}`}>{roleBadge[user.role]}</span>
            <div className={`status-led${isOnline ? '' : ' offline'}`} title={isOnline ? 'Server online' : 'Server offline — modalità locale'}/>
          </div>
        </div>
      </header>

      <div className="scroll-area">
        <div style={{padding:'0 12px'}}>
          <Notification events={todayEvents}/>
        </div>

        <div className="content">
          <div className="wake-bar">
            <div className="wake-info">
              <div className="wake-label">{t('wake.label')}{wakeListening && <span className="wake-active-dot"/>}</div>
              <div className="wake-sub">
                {!wakeSupported ? t('wake.not_supported')
                  : wakeWordEnabled ? wakeListening ? t('wake.listening') : t('wake.initializing')
                  : t('wake.hint')}
              </div>
            </div>
            <div className={`wake-switch ${wakeWordEnabled?'on':''} ${!wakeSupported?'disabled':''}`}
              onClick={()=>wakeSupported && setWakeWordEnabled(v=>!v)}>
              <div className="wake-thumb"/>
            </div>
          </div>

          <VoiceOrb state={voiceHook.state} volume={volume} onToggle={voiceHook.toggle} error={voiceHook.error}/>

          <div className="text-input-bar">
            <span className="ti-prompt">&gt;</span>
            <input ref={textRef} className="ti-inp" placeholder={t('input.placeholder')}
              value={textInput}
              onChange={e=>{ setTextInput(e.target.value); setIsTyping(e.target.value.length>0); }}
              onKeyDown={e=>e.key==='Enter'&&sendText()}
              disabled={voiceHook.state==='listening'||voiceHook.state==='processing'}/>
            <button className={`ti-send ${isTyping?'active':''}`} onClick={sendText} disabled={!textInput.trim()}>
              {t('input.send')}
            </button>
          </div>

          {userText && voiceHook.state!=='idle' && <div className="user-bubble">&gt; "{userText}"</div>}
          {apiError && <div className="api-error">⚠ {apiError}</div>}

          <div className="resp-box">
            <div className="resp-label">{t('output.label')}</div>
            <div className="resp-text">{lastMsg?.content}</div>
          </div>

          <div className="sec-label">{t('nav.quick_access')}</div>
          <div className="qgrid">
            {[
              {id:'cal',   icon:'📅', label:t('nav.cal'),  sub:`${todayEvents.length} ${t('nav.today')} · ${events.length} ${t('nav.total')}`},
              {id:'task',  icon:'▣',  label:t('nav.task'), sub:`${tasks.filter(t=>!t.done).length} ${t('nav.active')}`},
              {id:'gchat', icon:'💬', label:'GROUP CHAT',  sub:'chat di gruppo'},
              {id:'gshop',    icon:'🛒', label:'LISTA SPESA', sub:`${gItems.length} ${t('nav.items')}`},
              {id:'contacts', icon:'🕵🏻', label:'RUBRICA',     sub:`${contacts.length} contatti`, iconClass:'qicon-contacts'},
              {id:'orders',   icon:'🍽',  label:'ORDINI',      sub:`${orders.filter(o=>o.status==='active').length} attivi`},
            ].map(b=>(
              <div key={b.id} className={`qbtn ${panel===b.id?'qbtn-on':''}`} onClick={()=>openPanel(b.id)}>
                <div className={`qicon ${b.iconClass||''}`}>{b.icon}</div>
                <div><div className="qlabel">{b.label}</div><div className="qsub">{b.sub}</div></div>
              </div>
            ))}
          </div>

          {panel==='cal'   && <CalendarPanel events={events} onAdd={addEvent} onEdit={editEvent} onDelete={deleteEvent} onClose={()=>setPanel(null)} groupName={groupName} username={user.username}/>}
          {panel==='task'  && <TaskPanel tasks={tasks} onAdd={addTask} onToggle={toggleTask} onDelete={deleteTask} onEdit={editTask} onClose={()=>setPanel(null)} groupName={groupName} username={user.username}/>}
          {panel==='gchat' && <GroupChatPanel user={user} groupUsers={groupUsers} sendNotification={sendNotification} onClose={()=>setPanel(null)}/>}
          {panel==='gshop'    && <GroupShoppingPanel items={gItems} onAdd={gAdd} onRemove={gRemove} onEdit={gEdit} onClose={()=>setPanel(null)} groupName={groupName} username={user.username} productsDb={productsDb} onProductsDbChange={setProductsDb} isAdmin={user.role === 'admin'}/>}
          {panel==='contacts' && <ContactsPanel contacts={contacts} onAdd={addContact} onEdit={editContact} onDelete={deleteContact} onClose={()=>setPanel(null)}/>}
          {panel==='orders'   && <OrdersPanel orders={orders} onAdd={addOrder} onUpdate={updateOrder} onComplete={completeOrder} onDelete={deleteOrder} onClose={()=>setPanel(null)} user={user}/>}
          {panel==='admin' && <AdminPanel onClose={()=>setPanel(null)}/>}
        </div>
      </div>

      {showSettings && (
        <UserSettings
          onClose={() => setShowSettings(false)}
          isAdmin={user.role === 'admin'}
          user={user}
          onAdmin={() => { setShowSettings(false); setPanel('admin'); }}
          onUpdateUser={(newUser, newToken) => { storeSession(newToken, newUser); setUser(newUser); }}
        />
      )}

      {showNotifs && (
        <NotificationPanel
          notifications={notifications}
          onRespond={respondNotification}
          onClose={() => setShowNotifs(false)}
        />
      )}
      <NotificationToast message={toast} />

      <nav className="bnav">
        <div className={`bnav-txt bnav-settings${showSettings ? ' bnav-on' : ''}`}
             onClick={() => setShowSettings(v => !v)}>
          {t('nav.settings')}
        </div>
        <div className="bnav-txt bnav-bell">
          <NotificationBell count={pendingCount} onClick={() => setShowNotifs(v => !v)} />
        </div>
        <div className="bnav-txt bnav-logout" onClick={logout}>
          {t('header.logout')}
        </div>
      </nav>
    </div>
  );
}
